import React from 'react'
import { FormControl, FormHelperText, MenuItem, Select } from '@mui/material'
import FieldLabel from './FieldLabel'

export interface ChipSelectOption {
  value: string
  /** The coloured node shown for this option (e.g. a status chip). */
  render: React.ReactNode
}

export interface LabeledChipSelectProps {
  label: React.ReactNode
  value: string
  onChange: (value: string) => void
  options: ChipSelectOption[]
  required?: boolean
  helperText?: React.ReactNode
}

/**
 * A `FieldLabel` + `Select` whose options AND closed value render as coloured
 * nodes (chips) rather than plain text - so a status/enum dropdown carries its
 * colour. Reusable for any colour-coded enum: pass `options` with a `render`
 * node per value (e.g. `<FpStatusChip status={s} />`).
 */
const LabeledChipSelect: React.FC<LabeledChipSelectProps> = ({
  label,
  value,
  onChange,
  options,
  required = false,
  helperText,
}) => (
  <>
    <FieldLabel required={required}>{label}</FieldLabel>
    <FormControl fullWidth required={required}>
      <Select
        value={value}
        displayEmpty
        onChange={(e) => onChange(String(e.target.value))}
        renderValue={(val) => options.find((o) => o.value === val)?.render ?? null}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.render}
          </MenuItem>
        ))}
      </Select>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  </>
)

export default LabeledChipSelect
